import { put, takeEvery, takeLatest } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';

import {
  smevRequestCreate,
  smevRequestGetData,
  smevRequestSetData,
  smevRequestSetRequestTemplates,
  smevRequestSetRequestTypes,
} from '../actions/SmevRequestAction';
import { SmevRequestActionEnum } from '../enums/SmevRequestActionEnum';
import {
  createSmevMessage,
  fetchRequestTemplates,
  fetchRequestTypes,
  fetchSmevMessage,
} from '../../../APIPackage/SmevMessagesModule/services/smevMessagesApi';
import { ISmevMessageResponse } from '../../../APIPackage/SmevMessagesModule/interfaces/ISmevMessageResponse';
import { IRequestType } from '../../../APIPackage/SmevMessagesModule/interfaces/IRequestType';
import { IRequestTemplate } from '../../../APIPackage/SmevMessagesModule/interfaces/IRequestTemplate';
import { IResponseError } from '../../../APIPackage/MainModule/interfaces/IResponseError';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';

const NOT_FOUND_ERROR_CODE = 'E23001';

class SmevNotFoundError extends Error {
  readonly clientErrorCode = NOT_FOUND_ERROR_CODE;

  constructor() {
    super('SMEV message not found');
  }
}

export function* watchSmevRequestSaga() {
  yield takeLatest(SmevRequestActionEnum.SMEV_REQUEST_GET_DATA, smevMessageGetDataSaga);
  yield takeLatest(SmevRequestActionEnum.SMEV_REQUEST_CREATE, smevMessageCreateSaga);
  yield takeEvery(SmevRequestActionEnum.SMEV_REQUEST_GET_COMMON_DATA, smevMessageGetCommonDataSaga);
}

function isFound(response?: ISmevMessageResponse): boolean {
  return !!response && response.requestType != null;
}

function* tryFetchSmevMessage(searchValue: string, systemType: string) {
  try {
    return (yield fetchSmevMessage(searchValue, systemType)) as ISmevMessageResponse;
  } catch (err) {
    if ((err as IResponseError).clientErrorCode === NOT_FOUND_ERROR_CODE) {
      return undefined;
    }
    throw err;
  }
}

function* smevMessageGetDataSaga(action: ActionType<typeof smevRequestGetData>) {
  const { searchValue, systemType } = action.payload;

  function* funcTry() {
    let response: ISmevMessageResponse | undefined;

    if (systemType) {
      response = yield* tryFetchSmevMessage(searchValue!, systemType);
    } else {
      response = yield* tryFetchSmevMessage(searchValue!, 'erknm');
      if (!isFound(response)) {
        response = yield* tryFetchSmevMessage(searchValue!, 'erp');
      }
    }

    if (!isFound(response)) {
      throw new SmevNotFoundError();
    }

    yield put(smevRequestSetData(response));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(err: IResponseError) {
      const error: IResponseError = err as IResponseError;

      if (error.clientErrorCode === NOT_FOUND_ERROR_CODE) {
        yield put(
          addNoticeAction({
            renderContent: `Не найдено сообщение ${searchValue}`,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.SMEV_MESSAGE_GET_DATA_ERROR,
          }),
        );
      } else {
        yield put(
          addNoticeAction({
            renderContent: 'Ошибка при выполнении операции',
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.SMEV_MESSAGE_GET_DATA_ERROR,
          }),
        );
      }
    },
  });
}

function* smevMessageCreateSaga(action: ActionType<typeof smevRequestCreate>) {
  function* funcTry() {
    const { request, systemType } = action.payload;

    const response: ISmevMessageResponse = yield createSmevMessage(request, systemType);

    yield put(smevRequestSetData(response));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(err: IResponseError) {
      const error: IResponseError = err as IResponseError;

      yield put(
        addNoticeAction({
          renderContent: error.message,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.SMEV_MESSAGE_GET_DATA_ERROR,
        }),
      );
    },
  });
}

function* smevMessageGetCommonDataSaga() {
  function* funcTry() {
    const findRequestKinds: IRequestType[] = yield fetchRequestTypes();

    yield put(smevRequestSetRequestTypes(findRequestKinds));

    const findRequestTemplates: IRequestTemplate[] = yield fetchRequestTemplates();

    yield put(smevRequestSetRequestTemplates(findRequestTemplates));
  }

  yield sagaWrapper({
    funcTry,
  });
}
